import React, { useState } from 'react';
import { Alert, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import { useUpdateStatus } from '../hooks/useUpdateStatus';
import { safeReleaseUrl } from '../api/updateCheckApi';

/**
 * Ключ хранит ВЕРСИЮ, которую скрыли, а не булев флаг: иначе один клик по
 * «закрыть» глушил бы уведомления обо всех будущих релизах навсегда.
 */
const DISMISS_KEY = 'hub.updateNotice.dismissedVersion';

function readDismissed(): string | null {
  try {
    return window.localStorage.getItem(DISMISS_KEY);
  } catch {
    // localStorage недоступен (приватный режим, отключённое хранилище) —
    // баннер просто не запомнит закрытие. Это не повод падать.
    return null;
  }
}

/**
 * Баннер «доступна новая версия Hub» (#112).
 *
 * Рендерится только когда backend прямо сказал, что обновление есть. Ошибка
 * проверки НЕ показывается пользователю здесь: она видна администратору в
 * логе, а баннер про недоступный манифест — это шум на каждой загрузке
 * дашборда у инсталляции без egress.
 */
const UpdateAvailableBanner: React.FC = () => {
  const { t } = useTranslation();
  const { data } = useUpdateStatus();
  const [dismissed, setDismissed] = useState<string | null>(() => readDismissed());

  const latest = data?.latest_version;
  if (!data?.enabled || !data.update_available || !latest) return null;
  if (dismissed === latest) return null;

  const href = safeReleaseUrl(data.release_url);

  const handleClose = () => {
    setDismissed(latest);
    try {
      window.localStorage.setItem(DISMISS_KEY, latest);
    } catch {
      // Скрытие не переживёт перезагрузку — приемлемо, ронять UI из-за этого нет.
    }
  };

  return (
    <Alert
      type="info"
      showIcon
      closable
      onClose={handleClose}
      data-testid="update-available-banner"
      style={{ marginBottom: 16 }}
      message={t('updateNotice.title', { version: latest })}
      description={
        <Typography.Text type="secondary">
          {t('updateNotice.current', { version: data.current_version })}
          {href && (
            <>
              {' '}
              {/* rel="noreferrer" — ссылка ведёт на внешний хост, указанный
                  оператором; не передаём ему Referer с адресом инсталляции. */}
              <a href={href} target="_blank" rel="noreferrer">
                {t('updateNotice.releaseLink')}
              </a>
            </>
          )}
        </Typography.Text>
      }
    />
  );
};

export default UpdateAvailableBanner;
